import {
  DEFAULT_LOST_TIMEOUT_MS,
  DEFAULT_SCAN_RETRY_MS,
  LOST_WATCH_INTERVAL_MS,
  PROXIMITY_SERVICE_UUID,
} from '../constants';
import type {
  EngineLifecycleStatus,
  GpsPeerSource,
  NearbyPeer,
  PeerId,
  ProximityEvent,
  ProximityEventListener,
} from '../types';
import { resolveDeviceUwbSupported } from './uwbCapability';
import { BleAdvertiser } from './bleAdvertiser';
import { createCapacitorBleBridge, type ProximityBleBridge } from './bleBridge';
import { BleScanner } from './bleScanner';
import {
  clearBleGpsPeers,
  getBleGpsPeers,
  handleBlePeerGpsUpdate,
  mergeGpsSources,
  notifySelfPositionForBleBroadcast,
  onBleGpsPeersChange,
  stopBleGpsBroadcastTimer,
} from './blePeerGps';
import { createDeviceHeading, type DeviceHeadingController } from './deviceHeading';
import { applyGpsToPeers, createGpsOnlyNearbyPeer, enrichNearbyPeerWithGps } from './geodesy';
import type { GpsPeer } from './gpsPeer';
import { createCapacitorLocationBridge, type ProximityLocationBridge } from './locationBridge';
import type { LynxUwbUpdateEvent } from '../native/advertiserDefinitions';
import type { BearingResult } from './directionTypes';
import {
  defaultPeerNameResolver,
  forgetUwbDisplayHold,
  resetBleNearbyHold,
  resetUwbDisplayHold,
  resolvePeerDisplay,
  sortPeersByDisplay,
  type PeerDisplayInfo,
  type PeerNameResolver,
} from './peerDisplay';
import {
  applyScanObservation,
  canonicalizeRegistryToRoster,
  clearPeerRegistry,
  createPeerRegistry,
  findLostPeerIds,
  mergePeerAliases,
  rekeyPeer,
  removePeer,
  type PeerRegistry,
} from './peerRegistry';
import { getRemoteGpsPeers, onRemoteGpsPeersChange } from './peerGpsStore';
import {
  MIN_BLE_PEER_ID_LENGTH,
  peerIdMatches,
  resolveCanonicalPeerId,
  resolveRosterPeerId,
} from './peerId';
import { ProximityTracker } from './proximityTracker';
import { RssiProcessor } from './rssiProcessor';
import { createSelfPositionModule, type SelfPositionModule } from './selfPosition';

/** A UWB reading older than this means the NI session died without telling JS (see expireStaleUwb). */
const UWB_STALE_MS = 3000;

export type TrackedUwbReading = {
  active: boolean;
  distanceMeters: number | null;
  bearingDeg: number | null;
  directionAvailable: boolean;
  lastUpdatedAt: number | null;
};

export type ProximityEngineOptions = {
  /** Current user's opaque peer identifier for advertising. */
  localPeerId: string;
  serviceUuid?: string;
  lostTimeoutMs?: number;
  scanRetryMs?: number;
  /** Injectable native BLE bridge (defaults to Capacitor LynxAdvertiser). */
  bleBridge?: ProximityBleBridge;
  /** Injectable native location bridge for self-position (optional). */
  locationBridge?: ProximityLocationBridge;
  /** Start native GPS watch when engine starts (requires locationBridge). */
  enableSelfPosition?: boolean;
  /** Start compass heading when engine starts. */
  enableDeviceHeading?: boolean;
  /** Injectable clock (defaults to Date.now). */
  now?: () => number;
  /** Injectable RSSI processor (defaults to Lynx-tuned RssiProcessor). */
  rssiProcessor?: RssiProcessor;
  /** Injectable peer display name resolver (defaults to peer id passthrough). */
  nameResolver?: PeerNameResolver;
  /** Optional event sink in addition to subscribe(). */
  onEvent?: ProximityEventListener;
  /** Fired when native location watch reports an error (e.g. kCLErrorDenied). */
  onLocationError?: (event: { message: string }) => void;
  /**
   * When the session's event stops being live (epoch ms). Handed to native with the beacon, which will not advertise
   * or restore itself past it - before, a relaunch re-advertised the last user's beacon forever (2026-10-16 audit).
   */
  liveUntilMs?: number | null;
  /** Private beacon (docs/beacon-privacy.md): rotating ids and sealed GPS; needs setBeaconKeys. */
  beaconPrivacy?: boolean;
  /** Shadow test of the private beacon beside the legacy one (logging only). */
  beaconShadow?: boolean;
};

export interface ProximityEngine {
  readonly status: EngineLifecycleStatus;
  start(): Promise<void>;
  stop(): Promise<void>;
  /**
   * Off Find and in the background: stop the high-duty RSSI scan and our own UWB ranging. Advertising, the GATT
   * responder (userId, GPS, UWB token) and the native listeners stay up, so friends can still find this phone with
   * no internet (Turnstile test, 2026-10-14).
   */
  pauseBle(): Promise<void>;
  /** Resume the RSSI scan after pauseBle(). */
  resumeBle(): Promise<void>;
  readonly bleActive: boolean;
  subscribe(listener: ProximityEventListener): () => void;
  getPeers(): ReadonlyMap<PeerId, NearbyPeer>;
  trackUser(peerId: string): void;
  stopTracking(): void;
  getTrackedUserId(): string | null;
  getTrackedUwbReading(): TrackedUwbReading | null;
  getBearing(): BearingResult | null;
  getSelfPositionModule(): SelfPositionModule | null;
  getDeviceHeading(): DeviceHeadingController | null;
  getMergedGpsPeers(): GpsPeer[];
  /**
   * The full user ids being watched (the position subscriber's lynx_event_peers list), or null while
   * unknown. Every key a person is filed under resolves to one of these; anything else is dropped.
   */
  setRosterIds(ids: readonly PeerId[] | null): void;
  getRosterIds(): readonly PeerId[] | null;
  /** Day keys for the private beacon, handed to native (which keeps them for offline use). */
  setBeaconKeys(keys: readonly { userId: string; day: string; key: string }[]): void;
  resolvePeerDisplay(peerId: PeerId): PeerDisplayInfo | null;
  getSortedPeersWithDisplay(): { peer: NearbyPeer; display: PeerDisplayInfo }[];
}

export function createProximityEngine(options: ProximityEngineOptions): ProximityEngine {
  return new ProximityEngineImpl(options);
}

class ProximityEngineImpl implements ProximityEngine {
  private _status: EngineLifecycleStatus = 'idle';
  private readonly localPeerId: string;
  private readonly serviceUuid: string;
  private readonly lostTimeoutMs: number;
  private readonly scanRetryMs: number;
  private readonly now: () => number;
  private readonly rssiProcessor: RssiProcessor;
  private readonly bleBridge: ProximityBleBridge;
  private readonly nameResolver: PeerNameResolver;
  private readonly onEvent?: ProximityEventListener;
  private readonly onLocationError?: (event: { message: string }) => void;
  private readonly enableSelfPosition: boolean;
  private readonly enableDeviceHeading: boolean;
  private readonly liveUntilMs: number | null;
  private readonly beaconPrivacy: boolean;
  private readonly beaconShadow: boolean;

  private readonly listeners = new Set<ProximityEventListener>();
  private registry: PeerRegistry = createPeerRegistry();
  private rosterIds: PeerId[] | null = null;
  private scanner: BleScanner | null = null;
  private advertiser: BleAdvertiser | null = null;
  private lostTimer: ReturnType<typeof setInterval> | null = null;
  private scanLoopPromise: Promise<void> | null = null;
  private running = false;
  /** Advertising + native listeners are up (start() to stop(), including while paused). */
  private bleSubsystemActive = false;
  /** The foreground RSSI scan and our own UWB ranging are paused (off Find / background). */
  private scanPaused = false;

  private trackedPeerUserId: string | null = null;
  private uwbSessionPeerId: string | null = null;
  private uwbStartInFlightFor: string | null = null;
  private uwbLastUpdatedAt: number | null = null;
  private trackedBeforePause: string | null = null;
  private uwbLastEmitAt = 0;
  private uwbEmitTimer: ReturnType<typeof setTimeout> | null = null;
  private uwbUnsubs: Array<() => void | Promise<void>> = [];
  private bleGpsUnsub: (() => void | Promise<void>) | null = null;
  private gpsMergeUnsubs: Array<() => void> = [];

  private readonly selfPositionModule: SelfPositionModule | null;
  private readonly deviceHeading: DeviceHeadingController | null;
  private readonly proximityTracker: ProximityTracker;

  constructor(options: ProximityEngineOptions) {
    this.localPeerId = options.localPeerId;
    this.serviceUuid = options.serviceUuid ?? PROXIMITY_SERVICE_UUID;
    this.lostTimeoutMs = options.lostTimeoutMs ?? DEFAULT_LOST_TIMEOUT_MS;
    this.scanRetryMs = options.scanRetryMs ?? DEFAULT_SCAN_RETRY_MS;
    this.now = options.now ?? Date.now;
    this.rssiProcessor = options.rssiProcessor ?? new RssiProcessor();
    this.bleBridge = options.bleBridge ?? createCapacitorBleBridge();
    this.nameResolver = options.nameResolver ?? defaultPeerNameResolver;
    this.onEvent = options.onEvent;
    this.onLocationError = options.onLocationError;
    this.enableSelfPosition = options.enableSelfPosition ?? Boolean(options.locationBridge);
    this.enableDeviceHeading = options.enableDeviceHeading ?? true;
    this.liveUntilMs = options.liveUntilMs ?? null;
    this.beaconPrivacy = options.beaconPrivacy ?? false;
    this.beaconShadow = options.beaconShadow ?? false;

    if (options.locationBridge) {
      this.selfPositionModule = createSelfPositionModule({
        bridge: options.locationBridge,
        onPosition: (pos) => {
          if (this.bleSubsystemActive) {
            notifySelfPositionForBleBroadcast(this.bleBridge, pos, this.now);
          }
          this.refreshGpsOnPeers();
        },
        onLocationError: (event) => {
          this.onLocationError?.(event);
        },
      });
    } else {
      this.selfPositionModule = null;
    }

    if (this.enableDeviceHeading) {
      // Core Location's compass when there is a location bridge (iOS), which
      // costs no permission of its own and states which north it means. The
      // web DeviceOrientation fallback is what remains off-device.
      const bridge = options.locationBridge;
      this.deviceHeading = createDeviceHeading({
        nativeSource: bridge ? (handler) => bridge.addHeadingUpdateListener(handler) : null,
      });
    } else {
      this.deviceHeading = null;
    }

    this.proximityTracker = new ProximityTracker(this.rssiProcessor, {
      getHeadingDeg: () => this.deviceHeading?.getCurrent() ?? null,
    });
  }

  get status(): EngineLifecycleStatus {
    return this._status;
  }

  get bleActive(): boolean {
    return this.bleSubsystemActive && !this.scanPaused;
  }

  /** The RSSI scan loop and UWB ranging run only while BLE is up and not paused. */
  private get scanning(): boolean {
    return this.running && this.bleSubsystemActive && !this.scanPaused;
  }

  subscribe(listener: ProximityEventListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  getPeers(): ReadonlyMap<PeerId, NearbyPeer> {
    return this.registry.peers;
  }

  getSelfPositionModule(): SelfPositionModule | null {
    return this.selfPositionModule;
  }

  getDeviceHeading(): DeviceHeadingController | null {
    return this.deviceHeading;
  }

  getMergedGpsPeers(): GpsPeer[] {
    const roster = this.rosterIds;
    const canonical: GpsPeer[] = [];
    for (const gpsPeer of [...getRemoteGpsPeers(), ...getBleGpsPeers()]) {
      if (roster !== null) {
        // A GPS-over-Bluetooth fix filed under a cut-short or foreign id was a second dot with the
        // same position as the real one (2026-10-14 Turnstile).
        const member = resolveRosterPeerId(gpsPeer.id, roster);
        if (member !== null) {
          canonical.push(member === gpsPeer.id ? gpsPeer : { ...gpsPeer, id: member });
        }
      } else if (gpsPeer.source !== 'ble-gps' || gpsPeer.id.length >= MIN_BLE_PEER_ID_LENGTH) {
        canonical.push(gpsPeer);
      }
    }
    return mergeGpsSources(canonical, this.now());
  }

  setBeaconKeys(keys: readonly { userId: string; day: string; key: string }[]): void {
    if (keys.length > 0) {
      this.bleBridge.setBeaconKeys?.(keys)?.catch(() => undefined);
    }
  }

  setRosterIds(ids: readonly PeerId[] | null): void {
    const next =
      ids === null ? null : [...new Set(ids.filter((id) => id && id !== this.localPeerId))];
    const prev = this.rosterIds;
    if (
      prev !== null &&
      next !== null &&
      prev.length === next.length &&
      next.every((id) => prev.includes(id))
    ) {
      return;
    }
    this.rosterIds = next;
    // Native reconnects for GPS-over-Bluetooth only to these, and skips GATT to anyone else (2026-10-16 audit).
    this.bleBridge.setRosterIds?.(next)?.catch(() => undefined);
    if (this.running) {
      this.refreshGpsOnPeers();
    }
    this.emit({ type: 'roster-changed', rosterSize: next?.length ?? 0 });
  }

  getRosterIds(): readonly PeerId[] | null {
    return this.rosterIds;
  }

  resolvePeerDisplay(peerId: PeerId): PeerDisplayInfo | null {
    const peers = this.getPeersForDisplay();
    const peer =
      peers.find((candidate) => candidate.peerId === peerId) ??
      peers.find((candidate) => peerIdMatches(candidate.peerId, peerId));
    if (!peer) {
      return null;
    }
    return resolvePeerDisplay(peer, {
      now: this.now(),
      nameResolver: this.nameResolver,
      selfAccuracyMeters: this.selfPositionModule?.getSelfPosition()?.accuracyMeters ?? null,
    });
  }

  getSortedPeersWithDisplay(): { peer: NearbyPeer; display: PeerDisplayInfo }[] {
    const peers = this.getPeersForDisplay();
    const selfAccuracyMeters = this.selfPositionModule?.getSelfPosition()?.accuracyMeters ?? null;
    const displayById = new Map<string, PeerDisplayInfo>();
    for (const peer of peers) {
      displayById.set(
        peer.peerId,
        resolvePeerDisplay(peer, {
          now: this.now(),
          nameResolver: this.nameResolver,
          selfAccuracyMeters,
        }),
      );
    }
    return sortPeersByDisplay(peers, displayById).map((peer) => ({
      peer,
      display: displayById.get(peer.peerId)!,
    }));
  }

  /** BLE registry plus GPS-only peers from merged remote/BLE-GPS sources. */
  private getPeersForDisplay(): NearbyPeer[] {
    const self = this.selfPositionModule?.getSelfPosition() ?? null;
    const merged = this.getMergedGpsPeers();
    const byId = new Map<PeerId, NearbyPeer>();
    const roster = this.rosterIds;

    // One entry per roster user, whatever keys they reached us under.
    for (const [id, peer] of this.registry.peers) {
      const key = roster !== null ? resolveRosterPeerId(id, roster) : id;
      if (key === null) {
        continue;
      }
      const prior = byId.get(key);
      byId.set(
        key,
        prior ? mergePeerAliases(prior, peer, key) : key === id ? peer : { ...peer, peerId: key },
      );
    }

    if (!self || !Number.isFinite(self.lat) || !Number.isFinite(self.lng)) {
      return [...byId.values()];
    }

    const now = this.now();
    const gpsIds = merged.map((p) => p.id);
    for (const gpsPeer of merged) {
      if (!Number.isFinite(gpsPeer.lat) || !Number.isFinite(gpsPeer.lng)) {
        continue;
      }

      let existingKey: PeerId | undefined;
      for (const id of byId.keys()) {
        if (id === gpsPeer.id || resolveCanonicalPeerId(id, gpsIds) === gpsPeer.id) {
          existingKey = id;
          break;
        }
      }

      if (existingKey !== undefined) {
        // Shown under the full id whatever key Bluetooth filed them under.
        const existing = { ...byId.get(existingKey)!, peerId: gpsPeer.id };
        byId.delete(existingKey);
        existingKey = gpsPeer.id;
        const enriched =
          existing.gpsDistance !== undefined && Number.isFinite(existing.gpsDistance)
            ? existing.gpsSource !== undefined
              ? existing
              : { ...existing, gpsSource: gpsPeer.source }
            : enrichNearbyPeerWithGps(existing, gpsPeer, self, now);
        byId.set(existingKey, enriched);
        continue;
      }

      byId.set(gpsPeer.id, createGpsOnlyNearbyPeer(gpsPeer, self, now));
    }

    return [...byId.values()];
  }

  trackUser(peerId: string): void {
    peerId = this.canonicalPeerId(peerId);
    const was = this.trackedPeerUserId;
    // The same person only when the id we track resolves to this one: a prefix match alone could be a DIFFERENT friend
    // whose id starts the same (2026-10-12 audit), which must restart tracking and UWB.
    if (was !== null && (was === peerId || resolveCanonicalPeerId(was, [peerId]) === peerId)) {
      if (peerId !== was) {
        this.trackedPeerUserId = peerId;
        this.proximityTracker.startTracking(peerId); // RSSI readings now file under the full id
      }
      return;
    }
    this.trackedPeerUserId = peerId;
    this.proximityTracker.startTracking(peerId);
    void this.maybeStartUwbAfterTrack(peerId);
  }

  stopTracking(): void {
    const wasTracked = this.getTrackedUserId();
    if (wasTracked) forgetUwbDisplayHold(wasTracked);
    this.trackedPeerUserId = null;
    this.trackedBeforePause = null;
    this.clearUwbRetry();
    this.proximityTracker.stopTracking();
    void this.stopUwbNative();
    if (wasTracked) {
      this.clearUwbStateOnPeer(wasTracked);
    }
  }

  getTrackedUserId(): string | null {
    return this.trackedPeerUserId;
  }

  getTrackedUwbReading(): TrackedUwbReading | null {
    const tracked = this.getTrackedUserId();
    if (!tracked) {
      return null;
    }
    const peer = this.findPeerById(tracked);
    if (!peer) {
      return {
        active: false,
        distanceMeters: null,
        bearingDeg: null,
        directionAvailable: false,
        lastUpdatedAt: null,
      };
    }
    return {
      active: peer.uwbActive === true,
      distanceMeters: peer.uwbDistance ?? null,
      bearingDeg: peer.uwbBearing ?? null,
      directionAvailable: peer.uwbDirectionAvailable === true,
      lastUpdatedAt: this.uwbLastUpdatedAt,
    };
  }

  getBearing(): BearingResult | null {
    return this.proximityTracker.getBearing();
  }

  async start(): Promise<void> {
    if (this._status === 'running' || this._status === 'starting') {
      return;
    }
    this.setStatus('starting');

    try {
      this.running = true;
      this.bleSubsystemActive = true;
      this.scanPaused = false;
      this.registry = createPeerRegistry();
      this.rssiProcessor.clearAll();

      this.advertiser = new BleAdvertiser({
        bridge: this.bleBridge,
        localPeerId: this.localPeerId,
        serviceUuid: this.serviceUuid,
        liveUntilMs: this.liveUntilMs,
        beaconPrivacy: this.beaconPrivacy,
        beaconShadow: this.beaconShadow,
      });
      this.scanner = new BleScanner({
        bridge: this.bleBridge,
        onObservation: (observation) => this.handleScanObservation(observation),
      });

      await this.advertiser.start();
      await this.attachUwbListeners();
      await this.attachBleGpsListener();
      this.attachGpsMergeListeners();

      if (this.enableSelfPosition && this.selfPositionModule) {
        await this.selfPositionModule.startWatching();
      }
      if (this.enableDeviceHeading && this.deviceHeading) {
        this.deviceHeading.initialize();
      }

      this.startLostWatcher();
      this.scanLoopPromise = this.runScanLoop();
      this.refreshGpsOnPeers();

      void resolveDeviceUwbSupported(this.bleBridge);

      this.setStatus('running');
    } catch (err) {
      this.running = false;
      const message = err instanceof Error ? err.message : String(err);
      this.setStatus('error', message);
      throw err;
    }
  }

  async stop(): Promise<void> {
    if (this._status === 'idle' || this._status === 'stopping') {
      return;
    }
    this.setStatus('stopping');
    this.running = false;
    this.bleSubsystemActive = false;
    this.scanPaused = false;
    resetUwbDisplayHold(); // a held range belongs to this session
    resetBleNearbyHold();

    if (this.lostTimer !== null) {
      clearInterval(this.lostTimer);
      this.lostTimer = null;
    }

    if (this.scanLoopPromise) {
      await this.scanLoopPromise.catch(() => undefined);
      this.scanLoopPromise = null;
    }

    this.stopTracking();
    // Native too, whoever started it: an exchange still in flight, or a session a friend's write began, ran on after
    // the session ended (2026-10-16 audit).
    await this.stopUwbNative(true);
    if (this.uwbEmitTimer !== null) {
      clearTimeout(this.uwbEmitTimer);
      this.uwbEmitTimer = null;
    }
    await this.detachUwbListeners();
    await this.detachBleGpsListener();
    this.detachGpsMergeListeners();
    stopBleGpsBroadcastTimer();
    clearBleGpsPeers();

    if (this.selfPositionModule) {
      await this.selfPositionModule.stopWatching();
    }
    this.deviceHeading?.dispose();

    // Apart: a scanner.stop() that threw skipped stopAdvertising, and the beacon outlived the session (2026-10-16 audit).
    try {
      await this.scanner?.stop();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      this.emit({ type: 'error', message });
    }
    try {
      await this.advertiser?.stop();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      this.emit({ type: 'error', message });
    } finally {
      this.scanner = null;
      this.advertiser = null;
      this.registry = clearPeerRegistry(this.rssiProcessor);
      this.setStatus('idle');
    }
  }

  async pauseBle(): Promise<void> {
    if (this._status !== 'running' || !this.bleSubsystemActive || this.scanPaused) {
      return;
    }

    this.scanPaused = true;
    // Re-tracked on resume: the hook still shows them tracked, and before this nothing started UWB again.
    const trackedNow = this.getTrackedUserId();
    this.stopTracking();
    this.trackedBeforePause = trackedNow;
    await this.stopUwbNative();

    if (this.scanLoopPromise) {
      await this.scanLoopPromise.catch(() => undefined);
      this.scanLoopPromise = null;
    }

    // Only the scan stops. Advertising used to stop here too, and native stopAdvertising also forgot the persisted
    // beacon - so a friend off the Find page, or with the app in their pocket, went dark to Bluetooth. With no
    // internet that left only their last GPS row (Turnstile test, 2026-10-14). The UWB token responder and the
    // GATT GPS characteristic live on the same advertisement, so they stay up with it.
    try {
      await this.scanner?.stop();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      this.emit({ type: 'error', message });
    }

    this.refreshGpsOnPeers();
  }

  async resumeBle(): Promise<void> {
    if (this._status !== 'running' || !this.bleSubsystemActive || !this.scanPaused) {
      return;
    }

    if (!this.scanner) {
      this.scanner = new BleScanner({
        bridge: this.bleBridge,
        onObservation: (observation) => this.handleScanObservation(observation),
      });
    }

    try {
      // Idempotent natively; re-asserts the beacon in case Bluetooth was toggled while paused.
      await this.advertiser?.start();
      this.scanPaused = false;
      this.scanLoopPromise = this.runScanLoop();
      if (this.trackedBeforePause) {
        const again = this.trackedBeforePause;
        this.trackedBeforePause = null;
        this.trackUser(again);
      }

      const pos = this.selfPositionModule?.getSelfPosition();
      if (pos) {
        notifySelfPositionForBleBroadcast(this.bleBridge, pos, this.now);
      }
      this.refreshGpsOnPeers();
    } catch (err) {
      this.scanPaused = true;
      const message = err instanceof Error ? err.message : String(err);
      this.emit({ type: 'error', message });
      throw err;
    }
  }

  /** Exposed for unit tests to drive staleness without waiting on timers. */
  checkLostPeers(at?: number): void {
    this.evictLostPeers(at ?? this.now());
  }

  private setStatus(status: EngineLifecycleStatus, message?: string): void {
    this._status = status;
    this.emit({ type: 'status-changed', status, message });
  }

  private emit(event: ProximityEvent): void {
    this.onEvent?.(event);
    for (const listener of this.listeners) {
      listener(event);
    }
  }

  private startLostWatcher(): void {
    if (this.lostTimer !== null) {
      clearInterval(this.lostTimer);
    }
    this.lostTimer = setInterval(() => {
      if (!this.running) {
        return;
      }
      // Hidden: nothing is drawn, and the radios that feed the registry are paused (an idle web view, 2026-10-29).
      if (typeof document !== 'undefined' && document.hidden) {
        return;
      }
      this.evictLostPeers(this.now());
    }, LOST_WATCH_INTERVAL_MS);
  }

  private evictLostPeers(now: number): void {
    const lostIds = findLostPeerIds(this.registry, now, this.lostTimeoutMs);
    for (const peerId of lostIds) {
      this.registry = removePeer(this.registry, peerId, this.rssiProcessor);
      this.emit({ type: 'peer-lost', peerId });
    }
    this.expireStaleUwb(now);
  }

  /**
   * NI sessions die quietly: didInvalidateWith reaches Swift but nothing told JS, so the peer kept uwbActive with
   * its last range and the map pinned them there as "live" (2026-10-04 audit). A reading older than this is a dead
   * session: clear it, say so, and retry.
   */
  private expireStaleUwb(now: number): void {
    const tracked = this.getTrackedUserId();
    if (!tracked || this.uwbSessionPeerId === null || this.uwbLastUpdatedAt === null) {
      return;
    }
    if (now - this.uwbLastUpdatedAt <= UWB_STALE_MS) {
      return;
    }
    this.clearUwbStateOnPeer(tracked);
    this.uwbSessionPeerId = null;
    void this.stopUwbNative();
    this.emit({ type: 'uwb-updated', peerId: tracked, active: false });
    this.scheduleUwbRetry(tracked);
  }

  private handleScanObservation(observation: {
    deviceId: string;
    rssi: number;
    advertisedPeerId?: string;
  }): void {
    if (!this.running) {
      return;
    }

    const now = this.now();
    const { registry, outcome } = applyScanObservation(
      this.registry,
      observation,
      this.rssiProcessor,
      now,
      this.getMergedGpsPeers().map((p) => p.id),
      this.rosterIds,
    );
    this.registry = registry;

    if (outcome.kind === 'discovered') {
      this.emit({ type: 'peer-seen', peer: outcome.peer });
      const tracked = this.getTrackedUserId();
      if (
        tracked &&
        peerIdMatches(tracked, outcome.peer.peerId) &&
        this.uwbSessionPeerId === null &&
        this.uwbStartInFlightFor === null
      ) {
        void this.maybeStartUwbAfterTrack(tracked);
      }
    } else if (outcome.kind === 'updated') {
      this.emit({
        type: 'rssi-updated',
        sample: {
          peerId: outcome.peer.peerId,
          rssi: outcome.peer.smoothedRssi,
          observedAt: outcome.peer.lastSeenAt,
        },
      });
    }

    this.refreshGpsOnPeers();
  }

  private attachGpsMergeListeners(): void {
    this.detachGpsMergeListeners();
    this.gpsMergeUnsubs.push(
      onBleGpsPeersChange(() => this.refreshGpsOnPeers()),
      onRemoteGpsPeersChange(() => this.refreshGpsOnPeers()),
    );
  }

  private detachGpsMergeListeners(): void {
    for (const unsub of this.gpsMergeUnsubs) {
      unsub();
    }
    this.gpsMergeUnsubs = [];
  }

  /** The full user id for a possibly truncated BLE id, when exactly one known person fits it. */
  private canonicalPeerId(peerId: PeerId): PeerId {
    if (this.rosterIds !== null) {
      const member = resolveRosterPeerId(peerId, this.rosterIds);
      if (member !== null) {
        return member;
      }
    }
    return resolveCanonicalPeerId(peerId, [
      ...this.registry.peers.keys(),
      ...this.getMergedGpsPeers().map((p) => p.id),
    ]);
  }

  /**
   * Bluetooth often sees a friend before their first GPS row lands, under the truncated id iOS
   * advertises. Once the full id is known, the registry and every id we hold move to it, so names,
   * the map's dots and tracking all agree on one person (2026-10-11 Greek audit).
   */
  private adoptFullPeerIds(fullIds: PeerId[]): void {
    const roster = this.rosterIds;
    if (roster !== null) {
      this.registry = canonicalizeRegistryToRoster(this.registry, roster, this.rssiProcessor);
    } else {
      for (const id of [...this.registry.peers.keys()]) {
        const full = resolveCanonicalPeerId(id, fullIds);
        if (full !== id) {
          this.registry = rekeyPeer(this.registry, id, full, this.rssiProcessor);
        }
      }
    }
    const upgrade = (id: PeerId | null): PeerId | null =>
      id === null
        ? null
        : ((roster !== null ? resolveRosterPeerId(id, roster) : null) ??
          resolveCanonicalPeerId(id, fullIds));
    const trackedBefore = this.trackedPeerUserId;
    this.trackedPeerUserId = upgrade(this.trackedPeerUserId);
    // The bearing reads RSSI under the tracker's id; rekeyPeer cleared the truncated one (2026-10-12 audit).
    if (this.trackedPeerUserId !== null && this.trackedPeerUserId !== trackedBefore) {
      this.proximityTracker.startTracking(this.trackedPeerUserId);
    }
    this.trackedBeforePause = upgrade(this.trackedBeforePause);
    this.uwbSessionPeerId = upgrade(this.uwbSessionPeerId);
  }

  private refreshGpsOnPeers(): void {
    const self = this.selfPositionModule?.getSelfPosition() ?? null;
    const merged = this.getMergedGpsPeers();
    this.adoptFullPeerIds(merged.map((p) => p.id));
    const peers = applyGpsToPeers(self, merged, this.registry.peers, this.now());
    const nextRegistry = { ...this.registry, peers };
    const sourceById = new Map<string, GpsPeerSource>();
    for (const gpsPeer of merged) {
      sourceById.set(gpsPeer.id, gpsPeer.source);
    }

    const withSources = new Map<PeerId, NearbyPeer>();
    for (const [id, peer] of peers) {
      const source = sourceById.get(id);
      withSources.set(id, source !== undefined ? { ...peer, gpsSource: source } : peer);
    }

    this.registry = { ...nextRegistry, peers: withSources };
  }

  private async attachUwbListeners(): Promise<void> {
    await this.detachUwbListeners();
    this.uwbUnsubs.push(
      await this.bleBridge.addUwbUpdateListener((data) => this.handleUwbUpdate(data)),
      await this.bleBridge.addUwbPeerRemovedListener((data) => this.handleUwbPeerRemoved(data)),
      await this.bleBridge.addUwbSessionSuspendedListener(() => this.handleUwbSessionSuspended()),
      await this.bleBridge.addUwbTokenReceivedListener((data) => this.handleUwbTokenReceived(data)),
    );
  }

  private async detachUwbListeners(): Promise<void> {
    for (const unsub of this.uwbUnsubs) {
      await unsub();
    }
    this.uwbUnsubs = [];
  }

  private async attachBleGpsListener(): Promise<void> {
    await this.detachBleGpsListener();
    this.bleGpsUnsub = await this.bleBridge.addBlePeerGpsUpdateListener((event) => {
      handleBlePeerGpsUpdate(event);
    });
  }

  private async detachBleGpsListener(): Promise<void> {
    if (this.bleGpsUnsub) {
      await this.bleGpsUnsub();
      this.bleGpsUnsub = null;
    }
  }

  private handleUwbUpdate(data: LynxUwbUpdateEvent): void {
    const tracked = this.getTrackedUserId();
    if (!tracked) {
      return;
    }
    let candidate = data.userId?.trim() ?? '';
    if (!candidate) {
      candidate = this.registry.deviceToUser.get(data.deviceId) ?? '';
    }
    // Unresolved: dropped. Falling back to the tracked friend filed a reading from whoever wrote us a token under
    // the person on screen (2026-10-16 audit).
    if (!candidate || !peerIdMatches(tracked, candidate)) {
      return;
    }
    // Native rotates the NI direction into the world frame with CoreMotion and sends a compass bearing (`direction`)
    // only when the peer is clearly off the vertical; otherwise there is no direction. The old heading + device-frame
    // angle assumed an upright phone, so a phone held flat put a friend ahead or behind at 0 or 180 by noise
    // (Turnstile test, 2026-10-14). The bearing is absolute, so the radar's own heading lag cannot skew it.
    const bearing =
      data.directionAvailable === true && data.direction != null && Number.isFinite(data.direction)
        ? ((data.direction % 360) + 360) % 360
        : null;
    this.applyUwbReadingToPeer(tracked, data.distance, bearing, bearing !== null);
  }

  private handleUwbPeerRemoved(data: { deviceId: string; userId: string }): void {
    const tracked = this.getTrackedUserId();
    if (!tracked) {
      return;
    }
    const tDev = this.findDeviceIdForPeer(tracked);
    const userOk = data.userId.length > 0 && peerIdMatches(tracked, data.userId);
    const devOk = data.deviceId.length > 0 && tDev === data.deviceId;
    if (userOk || devOk) {
      this.clearUwbStateOnPeer(tracked);
      this.uwbSessionPeerId = null;
      this.emit({ type: 'uwb-updated', peerId: tracked, active: false });
      this.scheduleUwbRetry(tracked);
    }
  }

  private handleUwbSessionSuspended(): void {
    const tracked = this.getTrackedUserId();
    if (tracked) {
      this.clearUwbStateOnPeer(tracked);
      this.uwbSessionPeerId = null;
      this.emit({ type: 'uwb-updated', peerId: tracked, active: false });
      this.scheduleUwbRetry(tracked);
    }
  }

  /**
   * UWB used to be a one-shot: tap a friend, wait 10 s for their Bluetooth, give up for good. A friend who
   * walked into range a minute later, or a session iOS dropped, never got ranging again until the whole
   * session was rebuilt (2026-10-04). Now a dropped session is retried after a short pause while they are
   * still tracked, and a tracked friend first seen over Bluetooth starts one at once (handleScanObservation).
   */
  private uwbRetryTimer: ReturnType<typeof setTimeout> | null = null;
  private uwbRetryDelayMs = 3000;
  private scheduleUwbRetry(userId: string): void {
    if (this.uwbRetryTimer !== null) {
      clearTimeout(this.uwbRetryTimer);
    }
    const delayMs = this.uwbRetryDelayMs;
    // Back off (3 s, 6 s, 12 s, ... 30 s) so a session iOS keeps refusing does not churn; a reading resets it.
    this.uwbRetryDelayMs = Math.min(30_000, this.uwbRetryDelayMs * 2);
    this.uwbRetryTimer = setTimeout(() => {
      this.uwbRetryTimer = null;
      const tracked = this.getTrackedUserId();
      if (!this.scanning || !tracked || !peerIdMatches(tracked, userId)) {
        return;
      }
      void this.maybeStartUwbAfterTrack(tracked);
    }, delayMs);
  }

  private clearUwbRetry(): void {
    if (this.uwbRetryTimer !== null) {
      clearTimeout(this.uwbRetryTimer);
      this.uwbRetryTimer = null;
    }
  }

  /**
   * A friend's phone wrote its UWB token to ours. Native already ran the NI session with it (JS is suspended in the
   * background and its listeners were detached off Find, so the relay through here dropped tokens); this only notes
   * that ranging is up for the person we track.
   */
  private handleUwbTokenReceived(data: { deviceId: string; peerToken: string }): void {
    if (!data.deviceId?.length || !data.peerToken?.length) {
      return;
    }
    const tracked = this.getTrackedUserId();
    if (tracked) {
      this.uwbSessionPeerId = tracked;
    }
  }

  private applyUwbReadingToPeer(
    peerId: string,
    distance: number,
    bearing: number | null,
    directionAvailable: boolean,
  ): void {
    const peer = this.findPeerById(peerId);
    if (!peer) {
      return;
    }
    const next: NearbyPeer = {
      ...peer,
      uwbActive: true,
      uwbDistance: distance,
      uwbDirectionAvailable: directionAvailable,
    };
    if (bearing != null && Number.isFinite(bearing)) {
      next.uwbBearing = bearing;
    } else {
      delete next.uwbBearing;
    }
    this.registry = {
      ...this.registry,
      peers: new Map(this.registry.peers).set(peer.peerId, next),
    };
    this.uwbLastUpdatedAt = this.now();
    this.uwbRetryDelayMs = 3000;
    // Readings arrive several times a second; the map and the radar used to see them only on the next BLE
    // scan or GPS event (2026-10-04). Told at most every 150 ms - a row rebuild per reading is wasted work.
    const since = this.now() - this.uwbLastEmitAt;
    if (since >= 150) {
      this.uwbLastEmitAt = this.now();
      this.emit({ type: 'uwb-updated', peerId: peer.peerId, active: true });
    } else if (this.uwbEmitTimer === null) {
      this.uwbEmitTimer = setTimeout(() => {
        this.uwbEmitTimer = null;
        this.uwbLastEmitAt = this.now();
        this.emit({ type: 'uwb-updated', peerId: peer.peerId, active: true });
      }, 150 - since);
    }
  }

  private clearUwbStateOnPeer(peerId: string): void {
    const peer = this.findPeerById(peerId);
    if (!peer) {
      return;
    }
    const next: NearbyPeer = { ...peer, uwbActive: false };
    delete next.uwbDistance;
    delete next.uwbBearing;
    delete next.uwbDirectionAvailable;
    this.registry = {
      ...this.registry,
      peers: new Map(this.registry.peers).set(peer.peerId, next),
    };
    this.uwbLastUpdatedAt = null;
  }

  private findPeerById(peerId: string): NearbyPeer | undefined {
    for (const [id, peer] of this.registry.peers) {
      if (peerIdMatches(id, peerId)) {
        return peer;
      }
    }
    return undefined;
  }

  private findDeviceIdForPeer(peerId: string): string | undefined {
    for (const [deviceId, mappedId] of this.registry.deviceToUser) {
      if (peerIdMatches(mappedId, peerId)) {
        return deviceId;
      }
    }
    return undefined;
  }

  private async waitForBleDeviceId(
    userId: string,
    maxMs = 10_000,
    intervalMs = 50,
  ): Promise<string | null> {
    const deadline = this.now() + maxMs;
    while (this.now() < deadline) {
      const deviceId = this.findDeviceIdForPeer(userId);
      if (deviceId) {
        return deviceId;
      }
      await delay(intervalMs);
    }
    return null;
  }

  private async maybeStartUwbAfterTrack(userId: string): Promise<void> {
    if (this.uwbStartInFlightFor === userId) {
      return;
    }
    if (this.uwbSessionPeerId !== null && peerIdMatches(this.uwbSessionPeerId, userId)) {
      return;
    }

    this.uwbStartInFlightFor = userId;
    try {
      const supported = await resolveDeviceUwbSupported(this.bleBridge);
      if (!supported) {
        return;
      }
      if (this.uwbSessionPeerId !== null && !peerIdMatches(this.uwbSessionPeerId, userId)) {
        await this.stopUwbNative();
      }
      let deviceId = this.findDeviceIdForPeer(userId) ?? null;
      if (!deviceId) {
        deviceId = await this.waitForBleDeviceId(userId);
      }
      if (!deviceId) {
        return;
      }
      const result = await this.bleBridge.startUwbSession({ deviceId });
      if (this.uwbStartInFlightFor !== userId) {
        // Stopped while the exchange was in flight (not superseded by another friend, who owns native now): the
        // exchange carries on natively and would run NI after JS let go (2026-10-16 audit).
        if (result.ok && this.uwbStartInFlightFor === null) {
          await this.bleBridge.stopUwbSession();
        }
        return;
      }
      if (result.ok) {
        this.uwbSessionPeerId = userId;
      }
    } catch {
      /* UWB unavailable — engine continues RSSI-only */
    } finally {
      if (this.uwbStartInFlightFor === userId) {
        this.uwbStartInFlightFor = null;
      }
    }
  }

  /**
   * `force` (engine stop) calls native whatever JS believes: native may hold a session JS never started - a friend's
   * token write, or an exchange that finished after JS let go (2026-10-16 audit). Otherwise a session or a start in
   * flight is enough; a pause off Find leaves a friend's responder session alone.
   */
  private async stopUwbNative(force = false): Promise<void> {
    const hadSession = this.uwbSessionPeerId !== null || this.uwbStartInFlightFor !== null;
    this.uwbSessionPeerId = null;
    this.uwbStartInFlightFor = null;
    if (!hadSession && !force) {
      return;
    }
    try {
      await this.bleBridge.stopUwbSession();
    } catch {
      /* ignore stop errors */
    }
  }

  private async runScanLoop(): Promise<void> {
    while (this.scanning) {
      const scanner = this.scanner;
      if (!scanner) {
        break;
      }

      try {
        await scanner.start();
        while (this.scanning && scanner.isActive) {
          await delay(200);
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        this.emit({ type: 'error', message });
        await delay(this.scanRetryMs);
      } finally {
        try {
          await scanner.stop();
        } catch {
          /* ignore stop errors during loop teardown */
        }
      }

      if (!this.scanning) {
        break;
      }
    }
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Test-only surface for driving staleness without timers. */
export type ProximityEngineTestAccess = ProximityEngine & {
  checkLostPeers(at?: number): void;
};

export function asTestEngine(engine: ProximityEngine): ProximityEngineTestAccess {
  return engine as ProximityEngineTestAccess;
}

/** Default Capacitor location bridge factory for convenience. */
export { createCapacitorLocationBridge };
